// src/components/QuestionnaireDetailDialog.tsx
import React, { useEffect, useState } from 'react';
import api from '../api/axios';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';

type LangKey = 'ja' | 'zh';

interface QuestionItem {
  id: string;
  text: { ja: string; zh: string };
  type?: 'choice' | 'multi_choice' | 'short_text' | 'long_text' | 'number';
  options?: { ja: string[]; zh: string[] };
  required?: boolean;
}

type QuestionsRaw =
  | QuestionItem[]                                // 旧フォーマット：配列のみ
  | { first?: QuestionItem[]; followup?: QuestionItem[] }; // 新フォーマット：初診/再診に分岐

interface QuestionnaireDetailDialogProps {
  open: boolean;
  onClose: () => void;
  reservationId: number;
  service: string;                  // 例: "aga_male" など
  visitType?: 'first' | 'followup' | ''; // 就診タイプを明示する場合に利用（未指定なら自動判定）
}

/** Vite の BASE_URL に対応した静的パス生成 */
const dataUrl = (filename: string) => {
  // eslint-disable-next-line
  const base = process.env.PUBLIC_URL || '/';
  const prefix = base.endsWith('/') ? base : `${base}/`;
  return `${prefix}data/${filename}`;
};

/** 題庫ファイルを複数候補で読み込み（存在する最初の JSON を採用） */
async function loadQuestionsWithFallback(serviceName: string): Promise<{ raw: QuestionsRaw; used: string }> {
  const normalized = serviceName.trim();

  const candidates = Array.from(
    new Set(
      [
        `${normalized}_questions.json`,
        `${normalized.replace(/-/g, '_')}_questions.json`,
        `${normalized.replace(/_/g, '')}_questions.json`,
        normalized.includes('aga')
          ? `aga_${normalized.includes('female') ? 'female' : 'male'}_questions.json`
          : '',
        normalized.includes('obesity')
          ? `obesity_${normalized.includes('female') ? 'female' : 'male'}_questions.json`
          : '',
        (normalized === 'ed' || normalized === 'ed_premature') ? 'ed_premature_questions.json' : '',
        normalized === 'pill' ? 'pill_questions.json' : '',
      ].filter(Boolean)
    )
  );

  let lastErr: any = null;

  for (const name of candidates) {
    const url = dataUrl(name);
    try {
      const res = await fetch(url, { cache: 'no-store' });
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(`HTTP ${res.status} for ${url}. body: ${text.slice(0, 200)}`);
      }
      const text = await res.text();
      const json = JSON.parse(text) as QuestionsRaw;
      console.info(`[QuestionnaireDetailDialog] Loaded: ${url}`);
      return { raw: json, used: name };
    } catch (e) {
      lastErr = e;
      // 次候補へ
    }
  }

  throw lastErr || new Error('No question file matched.');
}

/** 表示に使う題の集合を決める（visitType 指定時は優先） */
function pickQuestions(
  raw: QuestionsRaw,
  answers: Record<string, any>,
  visitType?: 'first' | 'followup' | ''
): { list: QuestionItem[]; firstAll: QuestionItem[]; followAll: QuestionItem[] } {
  // first/followup の完全コピーも返して、後段の「題目テキスト補完」に活用する
  if (Array.isArray(raw)) {
    return { list: raw, firstAll: raw, followAll: [] };
  }
  const first = raw.first || [];
  const follow = raw.followup || [];

  if (visitType === 'first') {
    return { list: first.length ? first : follow, firstAll: first, followAll: follow };
  }
  if (visitType === 'followup') {
    return { list: follow.length ? follow : first, firstAll: first, followAll: follow };
  }

  if (!first.length && !follow.length) {
    return { list: [], firstAll: [], followAll: [] };
  }
  if (first.length && !follow.length) {
    return { list: first, firstAll: first, followAll: [] };
  }
  if (!first.length && follow.length) {
    return { list: follow, firstAll: [], followAll: follow };
  }

  // 自動判定：回答の「命中数」が多い方
  const countHit = (arr: QuestionItem[]) =>
    arr.reduce((n, q) => {
      const v = answers[q.id];
      const answered =
        v !== undefined && v !== null && !(typeof v === 'string' && v.trim() === '');
      return answered ? n + 1 : n;
    }, 0);

  const cFirst = countHit(first);
  const cFollow = countHit(follow);
  const chosen = cFollow > cFirst ? follow : first;

  return { list: chosen, firstAll: first, followAll: follow };
}

/** 値の文字列表現（配列は「、」連結） */
const toText = (v: any): string => {
  if (Array.isArray(v)) return v.join('、');
  if (v === null || v === undefined) return '';
  const s = String(v);
  return s;
};

const QuestionnaireDetailDialog: React.FC<QuestionnaireDetailDialogProps> = ({
  open,
  onClose,
  reservationId,
  service,
  visitType,
}) => {
  const { t } = useTranslation();
  const [qaList, setQaList] = useState<{ question: string; answer: string }[]>([]);
  const [error, setError] = useState('');
  const [notFilled, setNotFilled] = useState(false);

  useEffect(() => {
    if (!open) return;
    setNotFilled(false);

    const fetchData = async () => {
      try {
        // 1) 回答の取得
        const res = await api.get(`/questionnaire/detail/${reservationId}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });

        // 問診票未提出の場合
        if (res.data?.not_filled) {
          setNotFilled(true);
          setQaList([]);
          setError('');
          return;
        }

        // 回答は文字列/オブジェクトどちらも想定
        const answers: Record<string, any> =
          typeof res.data?.answers === 'string' ? JSON.parse(res.data.answers) : (res.data?.answers || {});

        // 2) 題庫読込
        const { raw } = await loadQuestionsWithFallback(service);

        // 3) 言語選択
        const lang: LangKey = i18n.language.startsWith('zh') ? 'zh' : 'ja';

        // 4) 題集合の決定（visitType 優先 / 未指定時は自動）
        const { list, firstAll, followAll } = pickQuestions(raw, answers, visitType);

        // 5) まずは「選ばれた題集合」をレンダリング
        const chosenIds = new Set<string>(list.map((q) => q.id));
        const parsedBase = list.map((q) => {
          const rawVal = answers[q.id];
          const val = toText(rawVal);
          return {
            question: q.text?.[lang] ?? q.id,
            answer: val && val.trim() ? val : (t('doctor.no_answer') as string),
          };
        });

        // 6) 題庫に存在しないが「回答に存在するキー」を追加表示（= 見落とし防止）
        //    → 旧版ファイル・別ファイルを読み込んだ場合でも、入力済みの項目が消えない
        const allKnown: Record<string, QuestionItem> = {};
        if (Array.isArray(raw)) {
          for (const q of raw) allKnown[q.id] = q;
        } else {
          for (const q of firstAll) allKnown[q.id] = q;
          for (const q of followAll) allKnown[q.id] = q;
        }

        const extras: { question: string; answer: string }[] = [];
        for (const key of Object.keys(answers)) {
          if (chosenIds.has(key)) continue; // 既に表示済み
          const val = toText(answers[key]);
          // 題目テキスト：題庫に見つかればそれ、なければ ID を出す
          const title =
            allKnown[key]?.text?.[lang] ??
            `${t('doctor.question', '質問')} (${key})`;

          extras.push({
            question: title,
            answer: val && val.trim() ? val : (t('doctor.no_answer') as string),
          });
        }

        // 7) ベース + 追加を結合（追加は末尾に）
        const finalList = parsedBase.concat(extras);

        setQaList(finalList);
        setError('');
      } catch (err: any) {
        console.error('❌ Failed to fetch questionnaire:', err);
        const friendly =
          err?.message?.includes('Unexpected') ||
            err?.message?.includes('HTTP 404') ||
            err?.message?.includes('DOCTYPE')
            ? i18n.language.startsWith('zh')
              ? '题库文件未找到或不是有效的 JSON，请检查 /public/data 下的文件名、JSON 结构与 service/visitType 是否一致。'
              : '質問票ファイルが見つからないか、JSON ではありません。/public/data のファイル名・JSON構造・service/visitTypeの一致をご確認ください。'
            : (t('doctor.load_error') as string);
        setError(friendly);
      }
    };

    fetchData();
  }, [open, reservationId, service, visitType, t]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
      <div className="bg-white rounded-xl p-6 max-w-2xl w-full shadow-lg overflow-y-auto max-h-[90vh]">
        <h2 className="text-xl font-bold mb-4">{t('mypage.view_questionnaire')}</h2>

        {error ? (
          <p className="text-red-500 text-center whitespace-pre-line">{error}</p>
        ) : notFilled ? (
          <p className="text-gray-500 text-center py-8">{t('doctor.questionnaire_not_filled')}</p>
        ) : (
          <ul className="space-y-4 max-h-[65vh] overflow-y-auto pr-2">
            {qaList.map((qa, index) => (
              <li key={index} className="border-b pb-3">
                <p className="font-semibold text-gray-800">
                  Q{index + 1}. {qa.question}
                </p>
                <p className="ml-4 text-gray-600 mt-1 whitespace-pre-line">A. {qa.answer}</p>
              </li>
            ))}
            {qaList.length === 0 && (
              <p className="text-gray-500 text-center">{t('doctor.no_answers')}</p>
            )}
          </ul>
        )}

        <div className="flex justify-end mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gradient-primary text-white rounded-full shadow-card transition-transform hover:-translate-y-0.5 whitespace-nowrap hover:bg-coral"
          >
            {t('mypage.close')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuestionnaireDetailDialog;
